import * as React from "react";
import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { observer } from "mobx-react-lite";
import "./OfferResultDialog.scss";
import useTranslation from "../../../hooks/useTranslation";
import { Offer, OfferStatus } from "../../../stores/OfferStore/types";
import authStore from "../../../stores/AuthStore";
import { Alert } from "@mui/material";
import { Link } from "react-router-dom";
import offerStore from "../../../stores/OfferStore";
import { LawyerInfo } from "../../LawyerInfo/LawyerInfo";
import { UserRoles } from "../../../stores/AuthStore/types";

interface OfferResultDialogProps {
  offers: Offer[];
  open: boolean;
  setOpen: (open: boolean) => void;
}

const OfferResultDialog = observer<OfferResultDialogProps>(
  ({ offers, open, setOpen }) => {
    const { t } = useTranslation();
    const userHasPhoneNumber = !!authStore.user.phone;
    const userHasLawyerRole = authStore.userHasRole(UserRoles.LAWYER);
    const approvedOffer = offers.find(
      (offer) => offer.status === OfferStatus.APPROVED
    );
    const [isAbortActive, setIsAbortActive] = useState(userHasLawyerRole);
    const [abortCounter, setAbortCounter] = useState(0);
    const [confirmApprove, setConfirmApprove] = useState<{
      [key: string]: boolean;
    }>({});

    useEffect(() => {
      if (approvedOffer && open && !userHasLawyerRole) {
        const timePassed =
          new Date().getTime() / 1000 -
          new Date(approvedOffer.approvalDate || "").getTime() / 1000;
        const timeLimitDiff = 48 * 60 * 60 - Math.floor(timePassed);

        if (timeLimitDiff > 0) {
          decreaseCounter(timeLimitDiff);
        } else {
          setIsAbortActive(true);
        }
      }
    }, [open, approvedOffer]);

    const decreaseCounter = (num: number) => {
      setAbortCounter(num);

      if (num === 0) {
        setIsAbortActive(true);
      } else {
        setTimeout(() => decreaseCounter(num - 1), 1000);
      }
    };

    const handleClose = () => {
      setOpen(false);
    };

    const handleReject = (offerId: string) => {
      offerStore.replyOffer(offerId, OfferStatus.REJECTED).then();
    };

    const handleAccept = (offerId: string) => {
      setConfirmApprove({
        ...confirmApprove,
        [offerId]: true,
      });
    };

    const handleCancelApprove = (offerId: string) => {
      setConfirmApprove({
        ...confirmApprove,
        [offerId]: false,
      });
    };

    const handleConfirmApprove = (offerId: string) => {
      setIsAbortActive(false);
      offerStore.replyOffer(offerId, OfferStatus.APPROVED).then(() => {
        offerStore.fetchUserOffers();
        setOpen(false);
      });
    };

    const handleAbort = (offerId: string) => {
      offerStore.abortOffer(offerId).then();
    };

    return (
      <Dialog open={open} onClose={handleClose} className="offerResultDialog">
        <DialogActions className="dialogActions">
          <Button onClick={handleClose} color={"error"}>
            &times;
          </Button>
        </DialogActions>
        <DialogContent>
          {!userHasPhoneNumber && (
            <Alert variant={"outlined"} severity={"error"} className={"mb"}>
              <div>{t("phone-required-to-accept-offer")}</div>
              <Link to={"/profile"}>{t("update-contact-info")}</Link>
            </Alert>
          )}
          {!!approvedOffer && (
            <>
              <header>{LawyerInfo(approvedOffer, t)}</header>
              <p className="lawyerMessage">
                <em>{approvedOffer.summary}</em>
              </p>
              <Alert variant={"outlined"} severity={"info"}>
                {t("started-agreement")}
              </Alert>
              <div className={"mt"}>
                <Button
                  fullWidth
                  disabled={!isAbortActive}
                  variant={"contained"}
                  color={"warning"}
                  onClick={() => handleAbort(approvedOffer.id || "")}
                >
                  <span>{t("abort-agreement")}</span>&nbsp;
                  {!isAbortActive && (
                    <span>
                      {Math.floor(
                        ((abortCounter - (abortCounter % 60)) / 3600) % 60
                      )}
                      :{((abortCounter - (abortCounter % 60)) / 60) % 60}:
                      {abortCounter % 60}
                    </span>
                  )}
                </Button>
              </div>
            </>
          )}
          {offers.length > 0 ? (
            offers
              .filter((offer) => offer.status !== OfferStatus.APPROVED)
              .map((offer) => (
                <>
                  <header>{LawyerInfo(offer, t)}</header>
                  {confirmApprove[offer?.id || ""] ? (
                    <Alert className="mt" severity="warning">
                      {t("confirm-offer-approve")}
                    </Alert>
                  ) : (
                    <p className="lawyerMessage">
                      <em>{offer.summary}</em>
                    </p>
                  )}
                  {offer.status === OfferStatus.PENDING && (
                    <DialogActions>
                      {confirmApprove[offer?.id || ""] ? (
                        <>
                          <Button
                            variant={"outlined"}
                            color={"error"}
                            onClick={() => handleCancelApprove(offer?.id || "")}
                          >
                            {t("cancel")}
                          </Button>
                          <Button
                            variant={"outlined"}
                            color={"success"}
                            onClick={() =>
                              handleConfirmApprove(offer?.id || "")
                            }
                            disabled={!userHasPhoneNumber}
                          >
                            {t("agree")}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant={"outlined"}
                            color={"error"}
                            onClick={() => handleReject(offer?.id || "")}
                          >
                            {t("reject")}
                          </Button>
                          <Button
                            variant={"outlined"}
                            color={"success"}
                            onClick={() => handleAccept(offer?.id || "")}
                            disabled={!userHasPhoneNumber}
                          >
                            {t("start-agreement")}
                          </Button>
                        </>
                      )}
                    </DialogActions>
                  )}
                  {offer.status === OfferStatus.REJECTED && (
                    <Alert variant={"outlined"} severity={"warning"}>
                      {t("you-rejected-offer")}
                    </Alert>
                  )}
                  {offer.status === OfferStatus.ON_HOLD && (
                    <Alert variant={"outlined"} severity={"warning"}>
                      {t("offer-on-hold")}
                    </Alert>
                  )}
                </>
              ))
          ) : (
            <div>{t("no-offer-for-case")}</div>
          )}
        </DialogContent>
      </Dialog>
    );
  }
);

export default OfferResultDialog;
